/**
 * The response chart's data: which resolution a window is drawn at, which
 * tiles of the series route hold it, and how tiles become plot rows, a status
 * lane and a stats line. Pure: no React, no fetch, no DOM. The chart's hooks
 * and components (`crm/sections/monitoring/response/`) only wire these to
 * events.
 *
 * Each rule has a way of being quietly wrong that a test catches and a glance
 * does not:
 *
 *  - The resolution follows the pixels, not the zoom level. A week of raw
 *    checks at five minutes is 2016 points on a 700 px plot: the line turns
 *    into a solid block and the browser draws points nobody can tell apart.
 *    So the step is the finest one whose points still get MIN_PX_PER_POINT
 *    pixels each. Raw is also capped at one raw read's window, or a site
 *    checked hourly would turn a week into 28 reads of six checks each.
 *  - The history is read in fixed, aligned tiles (`tileMs`), never in the
 *    window the reader happens to look at. A small scroll then asks for one
 *    new tile instead of the whole window again, and a tile already read is a
 *    cache hit whichever way the reader reached it.
 *  - A bucket is drawn at its middle. Drawn at its start, every bucketed line
 *    sits half a step early and a spike shows before the outage behind it.
 *  - A silence is a gap, not a slope. Two points further apart than the engine
 *    is ever quiet on a working site (it was off, the site was paused) get an
 *    empty row between them, so the line breaks instead of inventing the hours
 *    in between.
 *  - A bucketed median is a median of medians. It is flagged approximate
 *    instead of passing for the exact figure a raw window gives.
 *
 * Zoom, scroll and the history's length are `monitoringViewport.ts`,
 * re-exported here so the chart imports one path. When a tile in hand can
 * still change and how a refresh of it is read is `monitoringTiles.ts`. All
 * three reach the section through `monitoring.ts`. The route's limits below mirror
 * `apps/api/src/modules/crm-monitoring/lib/series.ts`, and `monitoring.test.ts`
 * holds them against that file's text.
 */
import type { MonitorDownSpan, MonitorSeriesPoint } from "./monitoring";
import { HOUR_MS, MINUTE_MS } from "./monitoringViewport";

export * from "./monitoringViewport";

// ── The series route's limits (mirror lib/series.ts) ─────────────────────────

/** Seconds per bucket the route accepts, finest first; 0 = raw checks, one point per check. */
export const SERIES_STEPS: readonly number[] = [0, 300, 900, 1800, 3600, 10_800, 21_600];
/** The longest raw window one read may cover, ms. */
export const SERIES_RAW_MAX_MS = 86_400_000;
/** The most buckets one bucketed read may hold. */
export const SERIES_MAX_BUCKETS = 1000;

/** A site's check interval in ms. The policy says 1..60 minutes; anything else reads as one. */
export const checkIntervalMs = (checkMinutes: number): number =>
  Math.max(1, Number.isFinite(checkMinutes) ? checkMinutes : 1) * MINUTE_MS;

/** Longer than this between two checks is a silence the line must not bridge: three
 *  intervals, never under a quarter hour (a failing site is re-checked every minute). */
const silenceMs = (checkMinutes: number) => Math.max(3 * checkIntervalMs(checkMinutes), 15 * MINUTE_MS);

// ── Resolution and tiles ─────────────────────────────────────────────────────

/** A point needs at least this many pixels, or the step gets coarser. */
export const MIN_PX_PER_POINT = 3;

/**
 * The finest step whose points in `span` fit `plotWidth / MIN_PX_PER_POINT`.
 * Raw is one point per check interval, and only for a window no longer than
 * one raw read (`SERIES_RAW_MAX_MS`): an hourly site's week fits raw on a wide
 * plot, but as 28 six-hour tiles of six checks each, so it takes hourly
 * buckets in one or two reads instead. A bucket step finer than the check
 * interval is skipped, because it would stand half empty. Falls back to the
 * coarsest step (also for a plot not measured yet, width 0).
 */
export function chooseStep(span: number, plotWidth: number, checkMinutes: number): number {
  const room = plotWidth / MIN_PX_PER_POINT;
  const interval = checkIntervalMs(checkMinutes);
  for (const step of SERIES_STEPS) {
    if (step === 0) {
      if (span <= SERIES_RAW_MAX_MS && span / interval <= room) return 0;
      continue;
    }
    if (step * 1000 < interval) continue;
    if (span / (step * 1000) <= room) return step;
  }
  return SERIES_STEPS[SERIES_STEPS.length - 1]!;
}

/** Raw tiles: at one-minute checks 360 rows, well inside the route's one-day raw limit. */
const RAW_TILE_MS = 6 * HOUR_MS;
/** Buckets per tile: about one plot width at 3 px a point, a quarter of the route's limit. */
const TILE_BUCKETS = 240;

/** Tile length per step: raw 6 h; bucketed 240 buckets (stays inside the route's limits). */
export function tileMs(step: number): number {
  return step === 0 ? RAW_TILE_MS : TILE_BUCKETS * step * 1000;
}

/**
 * Aligned tile starts covering [from, to). A tile is a multiple of its step, so
 * the server's own alignment of a bucketed read never widens it.
 */
export function tileStarts(step: number, from: number, to: number): number[] {
  if (!Number.isFinite(from) || !Number.isFinite(to) || to <= from) return [];
  const len = tileMs(step);
  const starts: number[] = [];
  for (let start = Math.floor(from / len) * len; start < to; start += len) starts.push(start);
  return starts;
}

export function tileKey(step: number, start: number): string {
  return `${step}:${start}`;
}

/** One read of the series route, [start, end). */
export interface SeriesTile {
  step: number;
  start: number;
  end: number;
  points: MonitorSeriesPoint[];
  downSpans: MonitorDownSpan[];
}

/**
 * Sort + merge touching or overlapping spans. A run that crosses a tile edge
 * arrives as two spans: the first tile closes it at the next tile's first
 * check, which is where the second one opens it, so touching counts.
 */
export function mergeSpans(spans: readonly MonitorDownSpan[]): MonitorDownSpan[] {
  const sorted = [...spans].sort((a, b) => a.from - b.from || a.to - b.to);
  const merged: MonitorDownSpan[] = [];
  for (const span of sorted) {
    const last = merged[merged.length - 1];
    if (last && span.from <= last.to) last.to = Math.max(last.to, span.to);
    else merged.push({ from: span.from, to: span.to });
  }
  return merged;
}

/** A span meets [from, to): one of zero length (a lone failure nothing came after) counts where it sits. */
const meets = (s: MonitorDownSpan, from: number, to: number) =>
  s.from < to && (s.to > from || (s.to === s.from && s.from >= from));

/**
 * Points of the tiles (of one step) inside [from, to), plus one neighbour on
 * each side so a line runs out of the frame instead of stopping short of its
 * edge; "inside" is by `pointX`, where the point is drawn. Spans are merged
 * across tiles, and only the ones that meet the view are kept, clipped to it.
 * Tiles may be given in any order; a point two tiles both hold counts once.
 */
export function viewSeries(
  tiles: readonly SeriesTile[],
  from: number,
  to: number,
): { points: MonitorSeriesPoint[]; downSpans: MonitorDownSpan[] } {
  const step = tiles[0]?.step ?? 0;
  const sorted = tiles.flatMap((tile) => tile.points).sort((a, b) => a.t - b.t);
  const all: MonitorSeriesPoint[] = [];
  for (const p of sorted) if (all[all.length - 1]?.t !== p.t) all.push(p);
  const firstIn = all.findIndex((p) => pointX(p, step) >= from);
  const lo = firstIn < 0 ? all.length : firstIn;
  const firstAfter = all.findIndex((p) => pointX(p, step) >= to);
  const hi = firstAfter < 0 ? all.length : firstAfter;
  const points = all.slice(Math.max(0, lo - 1), Math.min(all.length, hi + 1));
  const downSpans = mergeSpans(tiles.flatMap((tile) => tile.downSpans))
    .filter((s) => meets(s, from, to))
    .map((s) => ({ from: Math.max(s.from, from), to: Math.min(s.to, to) }));
  return { points, downSpans };
}

// ── Plot rows ────────────────────────────────────────────────────────────────

/** Where a point sits on the x axis: raw at t, a bucket at its middle (t + step/2). */
export function pointX(p: MonitorSeriesPoint, step: number): number {
  return step === 0 ? p.t : p.t + (step * 1000) / 2;
}

export interface ChartRow {
  x: number;
  p50: number | null;
  /** [fastest, p95] of a bucket that answered; the faint band behind the line. */
  band: [number, number] | null;
  /** The point behind the row, for the tooltip; null on a gap row. */
  point: MonitorSeriesPoint | null;
}

/**
 * Rows for the plot. p50 null where the site did not answer, which breaks the
 * line: a failed check has no time, and the red span says what happened. A gap
 * row (p50, band and point all null) goes between two points further apart
 * than a silence: raw, three check intervals and at least 15 min; bucketed,
 * 1.5 steps, never less than the raw silence, so a bucket step close to the
 * check interval does not break the line at every bucket a late check missed.
 * band = [min, p95] for bucketed points with an answer; null for raw (a raw
 * point has no spread).
 */
export function chartRows(points: readonly MonitorSeriesPoint[], step: number, checkMinutes: number): ChartRow[] {
  const gap = Math.max(1.5 * step * 1000, silenceMs(checkMinutes));
  const rows: ChartRow[] = [];
  let prev: MonitorSeriesPoint | null = null;
  for (const p of points) {
    if (prev && p.t - prev.t > gap) {
      rows.push({ x: (pointX(prev, step) + pointX(p, step)) / 2, p50: null, band: null, point: null });
    }
    const band: [number, number] | null = step > 0 && p.min !== null && p.p95 !== null ? [p.min, p.p95] : null;
    rows.push({ x: pointX(p, step), p50: p.p50, band, point: p });
    prev = p;
  }
  return rows;
}

// ── The status lane ──────────────────────────────────────────────────────────

export type LaneTone = "up" | "partial" | "down" | "blind";

/**
 * down if any down; else partial if some up and some blocked/unknown; else
 * blind if none up; else up. Blocked and unknown are "we could not see", never
 * an outage, so a bucket of them alone is blind rather than red.
 */
export function laneTone(p: MonitorSeriesPoint): LaneTone {
  if (p.down > 0) return "down";
  if (p.up === 0) return "blind";
  return p.blocked + p.unknown > 0 ? "partial" : "up";
}

/**
 * The status lane under the plot: contiguous same-tone runs clipped to
 * [from, to), from points oldest first. A raw check covers
 * [t, min(next.t, t + 1.5 * checkMinutes)); a bucket covers [t, t + step).
 * Gaps stay empty: a stretch nothing was checked in is not painted as fine.
 */
export function laneSegments(
  points: readonly MonitorSeriesPoint[],
  step: number,
  checkMinutes: number,
  from: number,
  to: number,
): { from: number; to: number; tone: LaneTone }[] {
  const reach = 1.5 * checkIntervalMs(checkMinutes);
  const runs: { from: number; to: number; tone: LaneTone }[] = [];
  for (let i = 0; i < points.length; i += 1) {
    const p = points[i]!;
    const next = points[i + 1];
    const end = step > 0 ? p.t + step * 1000 : Math.min(next ? next.t : Infinity, p.t + reach);
    const a = Math.max(p.t, from);
    const b = Math.min(end, to);
    if (b <= a) continue;
    const tone = laneTone(p);
    const last = runs[runs.length - 1];
    if (last && last.tone === tone && a <= last.to) last.to = Math.max(last.to, b);
    else runs.push({ from: a, to: b, tone });
  }
  return runs;
}

// ── The stats line ───────────────────────────────────────────────────────────

export interface ViewStats {
  checks: number;
  down: number;
  /** Blocked and unknown checks: the ones that could not tell. */
  blind: number;
  /** Exact for raw points; for buckets the median of bucket medians weighted by up count (approx = true). */
  median: number | null;
  approx: boolean;
  slowest: number | null;
  fastest: number | null;
  /** Length of the down spans clipped to the view, ms. */
  downMs: number;
}

/** Nearest-rank median of weighted values, the server's `percentile(values, 50)` when every weight is 1. */
function weightedMedian(values: { ms: number; weight: number }[]): number | null {
  if (values.length === 0) return null;
  const sorted = [...values].sort((a, b) => a.ms - b.ms);
  const total = sorted.reduce((sum, v) => sum + v.weight, 0);
  const rank = Math.max(1, Math.ceil(total / 2));
  let seen = 0;
  for (const v of sorted) {
    seen += v.weight;
    if (seen >= rank) return v.ms;
  }
  return sorted[sorted.length - 1]!.ms;
}

/**
 * The figures for what is on screen: points whose `pointX` is inside
 * [from, to), so the neighbours `viewSeries` adds for the line do not count.
 * The slowest and fastest are exact at any step (the max of the bucket maxes).
 */
export function viewStats(
  points: readonly MonitorSeriesPoint[],
  spans: readonly MonitorDownSpan[],
  step: number,
  from: number,
  to: number,
): ViewStats {
  let checks = 0;
  let down = 0;
  let blind = 0;
  let slowest: number | null = null;
  let fastest: number | null = null;
  const medians: { ms: number; weight: number }[] = [];
  for (const p of points) {
    const x = pointX(p, step);
    if (x < from || x >= to) continue;
    checks += p.n;
    down += p.down;
    blind += p.blocked + p.unknown;
    const hi = p.max ?? p.p50;
    const lo = p.min ?? p.p50;
    if (hi !== null && (slowest === null || hi > slowest)) slowest = hi;
    if (lo !== null && (fastest === null || lo < fastest)) fastest = lo;
    if (p.p50 !== null) medians.push({ ms: p.p50, weight: step === 0 ? 1 : Math.max(1, p.up) });
  }
  const median = weightedMedian(medians);
  const clipped = spans
    .map((s) => ({ from: Math.max(s.from, from), to: Math.min(s.to, to) }))
    .filter((s) => s.to > s.from);
  const downMs = mergeSpans(clipped).reduce((sum, s) => sum + (s.to - s.from), 0);
  return { checks, down, blind, median, approx: step > 0 && median !== null, slowest, fastest, downMs };
}
